import { Injectable } from '@angular/core';
import { NavController, Platform } from '@ionic/angular';
import { ServiceWorkerService } from '@takectrl-multiapp/svcs-core';
import {
  BUSNBrokerService,
  BUSNClaimsService,
  BUSNPolicyService,
  BUSNQuestionService,
  BUSNQuoteScheduleService,
  BUSNThirdPartyService,
  BUSNUserService,
} from '@takectrl-multiapp/svcs-core-business';
import { ApiAuthService, APISettingsService } from '@takectrl-multiapp/svcs-ctrl-api';
import { IAppSettings, IBrokerage, IUser, SharedConfig } from '@takectrl-multiapp/svcs-share-config';
import { IAPIAuthRequest } from 'libs/svcs-ctrl-api/src/lib/interfaces/auth.interface';
import { SharedConfigService } from 'libs/svcs-share-config/src/lib/services/shared-config.service';
import { firstValueFrom } from 'rxjs';

// Add this type definition at the top of your file or in a separate types file
type PlatformType =
  | 'mobileweb'
  | 'cordova'
  | 'desktop'
  | 'electron'
  | 'hybrid'
  | 'ios'
  | 'ipad'
  | 'mobile'
  | 'phablet'
  | 'pwa'
  | 'tablet'
  | 'iphone'
  | 'android'
  | 'capacitor';

@Injectable({
  providedIn: 'root',
})
export class UIAppInitializationService {
  constructor(
    public navCtrl: NavController,
    public busnUserService: BUSNUserService,
    public busnPolicyService: BUSNPolicyService,
    public busnQuoteScheduleService: BUSNQuoteScheduleService,
    public busnClaimsService: BUSNClaimsService,
    public platform: Platform,
    public busnQuestionService: BUSNQuestionService,
    public apiAuthService: ApiAuthService,
    public sharedConfig: SharedConfig,
    public sharedConfigService: SharedConfigService,
    public busnBrokerService: BUSNBrokerService,
    public apiSettingsService: APISettingsService,
    private readonly serviceWorkerService: ServiceWorkerService,
    private readonly busnThirdPartyService: BUSNThirdPartyService
  ) {}

  async hotReloadCore() {
    if (this.sharedConfig.showSetNewPasswordComponent) return;
    const intervalId = setInterval(() => {
      SharedConfig.intercomBadgeCount = document.querySelector('[data-testid="active-notifications"]')?.innerHTML;
      clearInterval(intervalId);
    }, 7000);

    try {
      this.sharedConfig.setShowCustomLoader(true, 'hotreload');

      await this.initializeFirebaseUser();

      if (!SharedConfig.firebaseUser) {
        await this.busnUserService.signOut();
        await this.navCtrl.navigateRoot(['public-landing']);
        return;
      }

      await this.initializeUserAndSettings();
      // const [user, brokerages] = await Promise.all([this.getUser(), this.getBrokerages()]);
      // await this.initializeIntercom(user, brokerages);

      // if (!this.sharedConfig.user.id) {
      //   const user = await this.getUser();
      // }
      // if (this.sharedConfig.quoteRequest === undefined || !this.sharedConfig.quoteRequest) {
      //   await this.busnUserService.doQuoteRequestDetailsRun();
      // }

      // START FIREBASE SUBSCRIBERS
      this.busnUserService.subscribeToFirebaseSubscriptionWatch(this.sharedConfig.user.id);
      // START FIREBASE SUBSCRIBERS
      this.sharedConfig.setShowCustomLoader(false, 'hotreload');
      this.sharedConfig.setDidHotReload(true);
    } catch (error) {
      console.error('Error in hotReloadCore:', error);
      this.sharedConfig.setShowCustomLoader(false, 'hotreload');
    }
  }

  private async initializeFirebaseUser(): Promise<void> {
    if (!SharedConfig.firebaseUser) {
      SharedConfig.firebaseUser = await this.busnThirdPartyService.firebaseCurrentUser();
      SharedConfig.firebaseUserKey = SharedConfig.firebaseUser?.uid;
      this.sharedConfig.setFirebaseUserEmail(SharedConfig.firebaseUser?.email);
    }
  }

  private async initializeUserAndSettings(): Promise<void> {
    if (!this.sharedConfig.user) {
      const user = await this.getUser();
      if (!user.insurance_history_id) {
        user.insurance_history = await this.busnUserService.createUserInsuranceHistory({ user_id: user.id });
        user.insurance_history_id = user.insurance_history.id;
      }
      this.sharedConfig.setUser(user);
      this.sharedConfigService.setUser(user);
    }

    this.getInsurers();

    if (!SharedConfig.didCheckPremiumChange && (this.sharedConfig.schedulesActive === undefined || this.sharedConfig.schedulesActive.length === 0)) {
      SharedConfig.didCheckPremiumChange = true;
      await this.busnUserService.setSchedules();
    }

    this.busnUserService.getPermissions().then((userPermissions) => {
      this.sharedConfig.setUserPermission(userPermissions);
    });
    this.preUserValidationInitialization();
    this.busnQuestionService.getUnderwritingQuestions().then((questions) => {
      SharedConfig.underwritingQuestionsAll = questions;
    });
  }

  getInsurers() {
    if (SharedConfig.insurers.length === 0) {
      this.busnPolicyService.getInsurers().then((insurers) => {
        SharedConfig.insurers.set(insurers);
      });
    }
    SharedConfig.schedulesActiveInitialCount = this.sharedConfig.schedulesActive?.length ?? 0;
    if (SharedConfig.claimCategories.length === 0) {
      this.busnClaimsService.getClaimCategories().then((claimCats) => {
        SharedConfig.claimCategories = claimCats;
      });
    }
  }

  private async initializeIntercom(user: IUser, brokerages: IBrokerage): Promise<void> {
    if (SharedConfig.intercomInitialized) return;
    try {
      this.busnThirdPartyService.initIntercom(brokerages, user);
    } catch (error) {
      console.error('Failed to initialize Intercom:', error);
      SharedConfig.intercomInitialized = false;
    }
  }

  async preUserValidationInitialization() {
    this.setPlatform();
    await this.getAppSettings();
  }

  async getAppSettings() {
    if (!SharedConfig.appSettingsRetrieved) {
      SharedConfig.appSettingsRetrieved = true;
      await this.setAppSettings();
    }
  }

  async setAppSettings(): Promise<IAppSettings> {
    const response = await firstValueFrom(this.apiSettingsService.getAppSettings());
    this.sharedConfig.setAppSettings(response.results);
    return response.results;
  }

  async getUser(): Promise<IUser> {
    return this.busnUserService.getUser(SharedConfig.firebaseUserKey);
  }

  async getBrokerages() {
    return this.busnBrokerService.getBrokerage();
  }

  async authAuthenticate(email: string, password: string): Promise<IAPIAuthRequest> {
    return firstValueFrom(this.apiAuthService.authAuthenticate(email, password));
  }

  setPlatform() {
    const platformTypes: PlatformType[] = [
      'mobileweb',
      'cordova',
      'desktop',
      'electron',
      'hybrid',
      'ios',
      'ipad',
      'mobile',
      'phablet',
      'pwa',
      'tablet',
      'iphone',
      'android',
      'capacitor',
    ];

    platformTypes.forEach((type) => {
      if (this.platform.is(type)) {
        SharedConfig.platformType = type;
      }
    });
  }
}
